import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Already imported, keep it here
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import CheckIcon from '@mui/icons-material/Check';
import Chip from '@mui/material/Chip';
import BarberSelection from '../components/BarberSelection';
import ServiceSelection from '../components/ServiceSelection';
import ExtraSelection from '../components/ExtraSelection';
import ItemSelection from '../components/ItemSelection';
import Button from '@mui/material/Button';
import FaceIcon from '@mui/icons-material/Face';
import CircularProgress from '@mui/material/CircularProgress';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import EventIcon from '@mui/icons-material/Event';
import { getUserDetails } from '../api/auth';
import { saveSession, getDailySessions, updateSession } from '../api/sessions';
import { Divider } from '@mui/material';
import { manageShift } from '../api/shift';


export default function Home() {
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [tip, setTip] = useState('');
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [shiftActive, setShiftActive] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [dailySessions, setDailySessions] = useState([]);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState(null);
  const [fromTime, setFromTime] = useState(null); // Store selected time for "from"
  const [toTime, setToTime] = useState(null); // Store selected time for "to"

  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const details = await getUserDetails();
        setUserDetails(details);

        const shiftResponse = await manageShift({ operation: 'check' });
        if (shiftResponse.shift) {
          setShiftActive(shiftResponse.shift.is_active);
        } else {
          setShiftActive(false);
        }

        const sessions = await getDailySessions();
        // Sort by created_at ascending (Eldest first)
        const sortedSessions = sessions.sort((a, b) => new Date(a.date) - new Date(b.date));
        setDailySessions(sortedSessions);
      } catch (error) {
        console.error('Error initializing:', error.message);
        setAlert({ message: 'Failed to load data.', severity: 'error' });
        setTimeout(() => setAlert(null), 5000);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);



  const handleSave = async () => {
    if (!userDetails) {
      setAlert({ message: 'Please log in to save sessions.', severity: 'error' });
      setTimeout(() => setAlert(null), 5000);
      return;
    }
  
    const totalPrice = calculateTotalPrice();
    if (totalPrice <= 0) {
      setAlert({ message: 'Please add some session details before saving.', severity: 'warning' });
      setTimeout(() => setAlert(null), 5000);
      return;
    }
  
    setSaving(true);
  
    try {
      const sessionData = {
        barberName: selectedBarber?.name || userDetails?.name || 'Unknown Barber',
        services: selectedServices,
        extras: selectedExtras,
        items: selectedItems,
        totalPrice,
        tip: parseFloat(tip) || 0,
        // Convert fromTime and toTime from Dayjs to Unix Timestamps
        fromTime: fromTime ? fromTime.valueOf() : null,  // using valueOf to get Unix timestamp
        toTime: toTime ? toTime.valueOf() : null,  // using valueOf to get Unix timestamp
      };
  
      console.log('Final sessionData before API call:', sessionData);
  
      if (selectedSessionIndex === null) {
        const saveResponse = await saveSession(sessionData);
        console.log('saveSession response:', saveResponse);
        const updatedSessions = await getDailySessions();
        const sortedSessions = updatedSessions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setDailySessions(sortedSessions);
        setAlert({ message: 'New session saved successfully!', severity: 'success' });
        navigate('/register'); // Use navigate here
      } else {
        const sessionId = dailySessions[selectedSessionIndex].id;
        console.log('Session ID to update:', sessionId);
        const updateResponse = await updateSession(sessionId, sessionData);
        console.log('updateSession response:', updateResponse);
        const updatedSessions = await getDailySessions();
        const sortedSessions = updatedSessions.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setDailySessions(sortedSessions);
        setAlert({ message: 'Session updated successfully!', severity: 'success' });
      }
  
      setTimeout(() => setAlert(null), 5000);
      if (selectedSessionIndex === null) resetForm();
    } catch (error) {
      console.error('Save/Update error:', error.response || error.message);
      setAlert({ message: `Error saving session: €{error.message}`, severity: 'error' });
      setTimeout(() => setAlert(null), 5000);
    } finally {
      setSaving(false);
    }
  };
  

  const handleCancel = () => {
    resetForm();
  };

  const resetForm = () => {
    setSelectedBarber(null);
    setSelectedServices([]);
    setSelectedExtras([]);
    setSelectedItems([]);
    setTip('');
    setFromTime(null);
    setToTime(null);
    setSelectedSessionIndex(null);
  };

  const handleSessionSelect = (index) => {
    setLoading(true);
    setTimeout(() => {
      if (index === null) {
        resetForm();
      } else {
        const session = dailySessions[index];
        console.log('Loading session:', session);
        setSelectedSessionIndex(index);
        setSelectedBarber({ name: session.barber_name });
        setSelectedServices(session.services || []);
        setSelectedExtras(session.extras || []);
        setSelectedItems(session.items || []);
        setTip(session.tip.toString());
        setFromTime(session.fromTime ? new Date(session.fromTime) : null);
        setToTime(session.toTime ? new Date(session.toTime) : null);
      }
      setLoading(false);
    }, 500);
  };

  const calculateTotalPrice = () => {
    const serviceTotal = selectedServices.reduce((total, service) => total + service.price, 0);
    const extraTotal = selectedExtras.reduce((total, extra) => total + extra.price, 0);
    const itemTotal = selectedItems.reduce((total, item) => total + item.price, 0);
    const tipTotal = parseFloat(tip) || 0;
    return serviceTotal + extraTotal + itemTotal + tipTotal;
  };

  const handleServiceSelection = (service) => {
    setSelectedServices((prev) =>
      prev.some((s) => s.name === service.name)
        ? prev.filter((s) => s.name !== service.name)
        : [...prev, service]
    );
  };

  const handleExtraSelection = (extra) => {
    setSelectedExtras((prev) =>
      prev.some((e) => e.name === extra.name)
        ? prev.filter((e) => e.name !== extra.name)
        : [...prev, extra]
    );
  };

  const handleItemSelection = (item) => {
    setSelectedItems((prev) =>
      prev.some((i) => i.name === item.name)
        ? prev.filter((i) => i.name !== item.name)
        : [...prev, item]
    );
  };

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {alert && (
        <Stack sx={{ mb: 3 }}>
          <Alert icon={<CheckIcon fontSize="inherit" />} severity={alert.severity}>
            {alert.message}
          </Alert>
        </Stack>
      )}

      {!shiftActive && (
        <Box sx={{ marginTop: '20%', textAlign: 'center' }}>
          <EventIcon />
          <Typography component="h2" variant="subtitle2" gutterBottom sx={{ fontWeight: '600' }}>
            Your Shift is not active
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: '8px' }}>
            Please make sure you start your shift before adding new customers
          </Typography>
        </Box>
      )}

      {shiftActive && (
        <>
        <Stack sx={{width: '100%', overflowX: 'auto'}}>
          <Stack
            direction="row"
            spacing={1} // Space between chips
            sx={{
              mb: 3,
              gap: '8px', // Add gap between rows
              flexWrap: {sx: 'nowrap', sm: 'wrap'}, // Prevent wrapping on small screens
            }}
          >
            <Chip
              icon={<AddCircleIcon sx={{ color: selectedSessionIndex === null ? 'white' : 'inherit' }} />}
              label="New Costumer"
              color={selectedSessionIndex === null ? 'primary' : 'default'}
              onClick={() => handleSessionSelect(null)}
              variant={selectedSessionIndex === null ? 'filled' : 'outlined'}
              sx={{
                cursor: 'pointer',
                pt: 2,
                pb: 2,
                pl: 1,
                pr: 1,
              }}
            />
            <Divider orientation="vertical" flexItem />
            {dailySessions.map((session, index) => (
              <Chip
                key={session.id}
                icon={<FaceIcon />}
                label={`Costumer ${index + 1}`}
                color={selectedSessionIndex === index ? 'primary' : 'default'}
                onClick={() => handleSessionSelect(index)}
                variant={selectedSessionIndex === index ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  pt: 2,
                  pb: 2,
                  pl: 1,
                  pr: 1,
                  marginLeft: '0 !important', // Prevent margin on the left of the first chip
                }}
              />
            ))}
          </Stack>
        </Stack>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {saving && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && !saving && (
            <>
              <BarberSelection selectedBarber={selectedBarber} setSelectedBarber={setSelectedBarber} />
              <ServiceSelection selectedServices={selectedServices} handleServiceSelection={handleServiceSelection} />
              <ExtraSelection selectedExtras={selectedExtras} handleExtraSelection={handleExtraSelection} />
              <ItemSelection selectedItems={selectedItems} handleItemSelection={handleItemSelection} />

              <Typography component="h2" variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
                Additional information
              </Typography>

              <Stack spacing={2} sx={{ mt: 3 }} direction="row">
                    <TextField
                      label="Tip (€)"
                      type="number"
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      value={tip}
                      onChange={(e) => setTip(e.target.value)}
                      fullWidth
                      variant="outlined"
                    />
                  </Stack>

                  <Divider sx={{ mt: 6, mb: 6 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }} alignContent="center">
                <Typography variant="h6">Total Price: €{calculateTotalPrice().toFixed(2)}</Typography>
                <Button color="primary" variant="outlined" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button  color="secondary" variant="outlined" onClick={handleSave}>
                  {selectedSessionIndex === null ? 'Save Session' : 'Update Session'}
                </Button>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
}